import { findReleaseDocument, type Job, type NotificationTask, type Release } from "@packages/commons";

export interface ReleaseNotificationJob extends Job {
  documentName: string;
}

export interface ReleaseNotificationTask extends NotificationTask {
  feedType: "notification";
  sortTime: number;
}

export interface ReleaseNotificationJobFeedItem extends ReleaseNotificationJob {
  feedType: "releaseJob";
  sortTime: number;
}

export type ReleaseNotificationFeedItem = ReleaseNotificationJobFeedItem | ReleaseNotificationTask;

export type LiveJobUpdate = Partial<Job> & Pick<Job, "id">;

export const getJobQueueStartedAt = (job: Job) => job.enqueuedAt ?? job.started;

export const getNotificationTaskCreatedAt = (notification: Pick<NotificationTask, "created" | "lastModified">) => {
  const value = notification.created ?? notification.lastModified;
  if (typeof value === "number") {
    return value;
  }
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const createJobFromUpdate = (jobUpdate: LiveJobUpdate): Job => ({
  status: "queued",
  started: jobUpdate.enqueuedAt ?? jobUpdate.started ?? jobUpdate.updated ?? Date.now(),
  tenancy: "",
  description: "",
  title: "Release job",
  payload: {} as any,
  user: {
    identity_id: "",
    first_name: "",
    last_name: "",
    full_name: "",
  },
  ...jobUpdate,
} as Job);

const countTrackedJobsAhead = (job: Job, jobs: Job[]) => {
  if (job.status !== "queued" || !job.releaseId) {
    return job.trackedJobsAhead;
  }
  return jobs.filter((candidate) => {
    return (
      candidate.id !== job.id
      && candidate.releaseId === job.releaseId
      && candidate.status === "queued"
      && getJobQueueStartedAt(candidate) < getJobQueueStartedAt(job)
    );
  }).length;
};

export const getVisibleTrackedJobsAhead = (job: Job, jobs: Job[]) => countTrackedJobsAhead(job, jobs);

export const buildReleaseNotificationJobs = (
  release: Pick<Release, "id" | "documents" | "jobs"> | null | undefined,
  liveJobUpdates: Record<string, LiveJobUpdate>,
): ReleaseNotificationJob[] => {
  if (!release?.id) {
    return [];
  }

  const jobs: ReleaseNotificationJob[] = [];
  const seenJobIds = new Set<string>();
  const addJob = (job: Job) => {
    const document = job.documentId ? findReleaseDocument(release.documents, (doc) => doc.reference === job.documentId) : undefined;
    jobs.push({
      ...job,
      documentId: job.documentId,
      documentName: document?.name ?? "",
    });
  };

  for (const job of release.jobs ?? []) {
    seenJobIds.add(job.id);
    addJob({
      ...job,
      ...liveJobUpdates[job.id],
    });
  }

  Object.values(liveJobUpdates).forEach((jobUpdate) => {
    if (!jobUpdate.id || seenJobIds.has(jobUpdate.id) || jobUpdate.releaseId !== release.id) {
      return;
    }
    addJob(createJobFromUpdate(jobUpdate));
  });

  const jobsWithQueueContext = jobs.map((job) => {
    if (typeof job.trackedJobsAhead === "number") {
      return job;
    }
    return {
      ...job,
      trackedJobsAhead: countTrackedJobsAhead(job, jobs),
    };
  });

  return jobsWithQueueContext.sort((a, b) => getJobQueueStartedAt(b) - getJobQueueStartedAt(a));
};

export const buildReleaseNotificationFeed = (
  release: Pick<Release, "id" | "documents" | "jobs" | "notifications"> | null | undefined,
  liveJobUpdates: Record<string, LiveJobUpdate>,
): ReleaseNotificationFeedItem[] => {
  const jobs = buildReleaseNotificationJobs(release, liveJobUpdates).map((job): ReleaseNotificationJobFeedItem => ({
    ...job,
    feedType: "releaseJob",
    sortTime: getJobQueueStartedAt(job),
  }));
  const notifications = (release?.notifications ?? []).map((notification): ReleaseNotificationTask => ({
    ...notification,
    feedType: "notification",
    sortTime: getNotificationTaskCreatedAt(notification),
  }));

  return [...jobs, ...notifications].sort((a, b) => b.sortTime - a.sortTime);
};

export const isReleaseJobNotification = (
  notification: ReleaseNotificationFeedItem,
): notification is ReleaseNotificationJobFeedItem => notification.feedType === "releaseJob";

export const isTaskNotification = (
  notification: ReleaseNotificationFeedItem,
): notification is ReleaseNotificationTask => notification.feedType === "notification";

export const getReleaseJobDisplayStatus = (job: Job) => {
  if (job.status === "queued" && job.processingStartedAt) {
    return "Processing";
  }
  return job.status.charAt(0).toUpperCase() + job.status.slice(1);
};

export const formatReleaseJobDuration = (value: number | undefined) => {
  if (!Number.isFinite(value) || typeof value !== "number" || value < 0) {
    return "unknown";
  }
  if (value < 1000) {
    return "<1s";
  }
  if (value < 60 * 1000) {
    return `${Math.round(value / 1000)}s`;
  }
  const minutes = Math.floor(value / 60000);
  const seconds = Math.round((value % 60000) / 1000);
  if (minutes < 60) {
    return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
};

export const getReleaseJobTimingText = (job: Job, now = Date.now()) => {
  const queueStartedAt = getJobQueueStartedAt(job);
  const queueDuration = job.queueDurationMs ?? (
    job.processingStartedAt
      ? Math.max(0, job.processingStartedAt - queueStartedAt)
      : Math.max(0, now - queueStartedAt)
  );
  const processingDuration = job.processingDurationMs ?? (
    job.processingStartedAt
      ? Math.max(0, (job.processingFinishedAt ?? now) - job.processingStartedAt)
      : undefined
  );

  if (job.status === "queued" && !job.processingStartedAt) {
    const queueParts = [`Waiting ${formatReleaseJobDuration(queueDuration)}`];
    if (typeof job.trackedJobsAhead === "number") {
      queueParts.push(
        job.trackedJobsAhead === 0
          ? "No tracked jobs ahead in the queue"
          : `${job.trackedJobsAhead} tracked job${job.trackedJobsAhead === 1 ? "" : "s"} ahead`,
      );
    }
    return queueParts.join(" · ");
  }

  if (job.status === "queued" && job.processingStartedAt) {
    return `Waited ${formatReleaseJobDuration(queueDuration)} · Running ${formatReleaseJobDuration(processingDuration)}`;
  }

  if (job.processingStartedAt || job.queueDurationMs || job.processingDurationMs) {
    return `Waited ${formatReleaseJobDuration(queueDuration)} · Ran ${formatReleaseJobDuration(processingDuration)}`;
  }

  return `Updated ${formatReleaseJobDuration(Math.max(0, now - (job.updated ?? queueStartedAt)))} ago`;
};

export const getNotificationTaskTimingText = (notification: ReleaseNotificationTask) => {
  const created = getNotificationTaskCreatedAt(notification);
  const parts = [`Created ${new Date(created).toLocaleString()}`];
  if (notification.dueDate) {
    parts.push(`Due ${new Date(notification.dueDate).toLocaleDateString()}`);
  }
  return parts.join(" · ");
};
